import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { AdminBackButton } from "./AdminBackButton";
import {
  LayoutDashboard, ShoppingBag, ClipboardList, Truck, ShieldAlert,
  PackageCheck, Users, Crown, BoxSelect, LogOut, AlertOctagon, FilePlus2, Package,
  Hourglass, PackageOpen, MessageCircle, Megaphone, Mail, Shield, Sparkles, Database, FileText,
  Menu, X, Search, CalendarDays, MapPin,
} from "lucide-react";

/**
 * /console layout , v3 (Feb 2026, Kayla redesign).
 *
 * Visual brief: bold, straightforward dashboard. Deep aubergine sidebar with
 * the Heritage seal at the top. Serif everywhere. Main column on a clean
 * parchment-cream base; section panels can render the season stained-glass
 * tiles as their backdrop.
 */
const SEAL = "/photos/brand/heritage-seal.png";

const NAV = [
  { group: "Today", items: [
    { to: "/console", label: "Dashboard", Icon: LayoutDashboard, end: true, testid: "console-nav-dashboard" },
    { to: "/console/financial-summary", label: "Financial Summary", Icon: BoxSelect, testid: "console-nav-fin-summary" },
  ]},
  { group: "Orders", items: [
    { to: "/console/orders/awaiting_payment",   label: "Awaiting Payment", Icon: Hourglass,    testid: "console-nav-awaiting-payment" },
    { to: "/console/orders/packing",            label: "Packing",          Icon: PackageOpen,  testid: "console-nav-packing" },
    { to: "/console/orders/shipping",           label: "Shipping",         Icon: Truck,        testid: "console-nav-shipping" },
    { to: "/console/orders/qc",                 label: "Quality Control",  Icon: ShieldAlert,  testid: "console-nav-qc" },
    { to: "/console/orders/completed",          label: "Completed",        Icon: PackageCheck, testid: "console-nav-completed" },
  ]},
  { group: "Operations", items: [
    { to: "/console/jobs",             label: "Jobs",            Icon: CalendarDays, testid: "console-nav-jobs" },
    { to: "/console/delivery-routes",  label: "Delivery Routes", Icon: MapPin,       testid: "console-nav-delivery-routes" },
  ]},
  { group: "Reports", items: [
    { to: "/console/reports",       label: "Monthly Reports",   Icon: ClipboardList, testid: "console-nav-reports" },
  ]},
];

const AUBERGINE = "#3E1F46";
const AUBERGINE_DEEP = "#2C1531";
const GOLD = "#C9A268";
const CREAM = "#FBF6E9";
const PARCHMENT = "#F4ECD8";

export default function ConsoleLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem("heritage-admin-token");
    navigate("/admin/login");
  };

  return (
    <div
      className="min-h-screen relative"
      data-testid="console-layout"
      style={{ backgroundColor: CREAM, color: "#2A1F1A" }}
    >
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: AUBERGINE, borderColor: AUBERGINE_DEEP }}>
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          data-testid="console-mobile-menu"
          className="inline-flex items-center gap-2 text-white"
          aria-label="Open navigation"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[11px] tracking-[0.3em] uppercase font-semibold">Menu</span>
        </button>
        <div className="text-right leading-tight text-white">
          <div className="text-[9px] tracking-[0.35em] uppercase opacity-80">Heritage Dahlias</div>
          <div className="font-serif text-base">Console</div>
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[280px_1fr] min-h-screen relative">
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            data-testid="console-mobile-backdrop"
          />
        )}

        {/* Sidebar , deep aubergine */}
        <aside
          className={
            "flex flex-col fixed lg:static inset-y-0 left-0 z-50 w-[280px] lg:w-auto " +
            "transform transition-transform duration-200 ease-out lg:transform-none " +
            (sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
          }
          data-testid="console-sidebar"
          style={{
            backgroundColor: AUBERGINE,
            backgroundImage: `linear-gradient(180deg, ${AUBERGINE} 0%, ${AUBERGINE_DEEP} 100%)`,
            color: "#F4ECD8",
          }}
        >
          {/* Brand */}
          <div className="px-6 py-6 border-b flex items-center gap-3" style={{ borderColor: "rgba(244,236,216,0.12)" }}>
            <div
              className="w-14 h-14 shrink-0 rounded-full flex items-center justify-center"
              style={{ backgroundColor: CREAM, boxShadow: `0 0 0 2px ${GOLD}` }}
            >
              <img src={SEAL} alt="Heritage Dahlias" className="w-12 h-12" />
            </div>
            <div className="leading-tight">
              <div className="text-[9px] tracking-[0.4em] uppercase opacity-70 font-semibold">Heritage Dahlias</div>
              <div className="font-serif text-2xl" style={{ color: CREAM, letterSpacing: "0.02em" }}>Console</div>
              <div className="text-[10px] tracking-[0.18em] uppercase opacity-80 mt-0.5" style={{ color: GOLD }}>Management</div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden ml-auto text-[#F4ECD8]/70 hover:text-[#F4ECD8]"
              aria-label="Close navigation"
              data-testid="console-mobile-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-4 px-2">
            {NAV.map((group) => (
              <div key={group.group} className="mb-5">
                <div className="px-4 mb-1.5 text-[9px] tracking-[0.4em] uppercase font-semibold" style={{ color: GOLD, opacity: 0.75 }}>
                  {group.group}
                </div>
                {group.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={it.testid}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      "mx-2 px-3 py-2 my-0.5 flex items-center gap-2.5 rounded-sm text-[13px] font-serif transition " +
                      (isActive
                        ? "bg-white/95 text-[#3E1F46] font-semibold shadow-sm"
                        : "text-[#F4ECD8]/85 hover:bg-white/10 hover:text-white")
                    }
                  >
                    <it.Icon className="w-4 h-4 opacity-80 shrink-0" />
                    <span className="truncate">{it.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="p-4 border-t space-y-2" style={{ borderColor: "rgba(244,236,216,0.12)" }}>
            <button
              type="button"
              onClick={logout}
              className="w-full text-[11px] tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 opacity-70 hover:opacity-100"
              data-testid="console-logout"
              style={{ color: CREAM }}
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex flex-col min-h-screen min-w-0">
          {/* Top bar with search */}
          <div className="hidden lg:flex items-center justify-between px-8 py-4 border-b" style={{ backgroundColor: PARCHMENT, borderColor: "#E8DFCB" }}>
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <Search className="w-4 h-4 text-[#7A6A55]" />
              <input
                type="text"
                placeholder="Search orders, customers, varieties…"
                data-testid="console-topbar-search"
                className="flex-1 bg-transparent border-0 outline-none text-sm font-serif placeholder:text-[#9C8B73]"
                style={{ color: "#3C2A1E" }}
              />
            </div>
            <div className="text-right leading-tight">
              <div className="text-[9px] tracking-[0.4em] uppercase opacity-60">Logged in</div>
              <div className="font-serif text-sm" style={{ color: AUBERGINE }}>Heritage Admin</div>
            </div>
          </div>
          <main className="p-4 sm:p-6 lg:p-10 max-w-full w-full min-w-0 overflow-x-hidden">
            <AdminBackButton />
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
